import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';
import { applyTreatmentPagePatch, mergeTreatmentPagePatches } from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3TreatmentPage,
  UpdateTreatmentPlanV3TreatmentPagePayload,
  TreatmentPlanV3TreatmentThunks,
  TreatmentMutationAction,
} from './types';

export function addTreatmentPlanV3TreatmentCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TreatmentThunks,
) {
  addGetTreatmentPlanV3TreatmentPageCases(builder, thunks);
  addUpdateTreatmentPlanV3TreatmentPageCases(builder, thunks);
}

function isForLoadedTreatmentPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.treatmentPageTreatmentPlanId;
}

function addGetTreatmentPlanV3TreatmentPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TreatmentThunks,
) {
  const { getTreatmentPlanV3TreatmentPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3TreatmentPage.pending, (state, action) => {
      if (!isForLoadedTreatmentPage(state, action)) {
        state.treatmentPage = null;
      }
      state.treatmentPageLoading = true;
      state.treatmentPageError = null;
      state.treatmentPageRequestId = action.meta.requestId;
      state.treatmentPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.treatmentPagePatchesSinceRequest = [];
      state.error = null;
    })
    .addCase(getTreatmentPlanV3TreatmentPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.treatmentPageRequestId) return;
      state.treatmentPageLoading = false;
      state.treatmentPageError = null;
      state.treatmentPage = mergeTreatmentPagePatches(
        action.payload,
        state.treatmentPagePatchesSinceRequest,
      );
      state.treatmentPagePatchesSinceRequest = [];
      state.treatmentPageLoadCount += 1;
    })
    .addCase(getTreatmentPlanV3TreatmentPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.treatmentPageRequestId) return;
      state.treatmentPageLoading = false;
      state.treatmentPageError = action.error;
      state.treatmentPagePatchesSinceRequest = [];
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateTreatmentPlanV3TreatmentPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TreatmentThunks,
) {
  const { updateTreatmentPlanV3TreatmentPage } = thunks;
  const flag = 'updateTreatmentPageLoading';

  builder
    .addCase(updateTreatmentPlanV3TreatmentPage.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3TreatmentPage.fulfilled,
      (
        state,
        action: TreatmentMutationAction<
          ITreatmentPlanV3TreatmentPage,
          UpdateTreatmentPlanV3TreatmentPagePayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedTreatmentPage(state, action)) return;
        // The in-flight GET may have read the page before this update committed.
        if (state.treatmentPageLoading) {
          state.treatmentPagePatchesSinceRequest.push({
            data: action.meta.arg.data,
            payload: action.payload,
          });
        }
        if (!state.treatmentPage) {
          state.treatmentPage = action.payload;
          return;
        }
        applyTreatmentPagePatch(state.treatmentPage, action.payload, action.meta.arg.data);
      },
    )
    .addCase(updateTreatmentPlanV3TreatmentPage.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedTreatmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
